

import React, { useState, useEffect } from "react";
import {
  Card,
  Descriptions,
  Tag,
  Button,
  Space,
  Typography,
  Divider,
  Row,
  Col,
  Statistic,
  Timeline,
} from "antd";
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  CreditCardOutlined,
  BankOutlined,
  PayCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UndoOutlined,
  EnvironmentOutlined,
  IdcardOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useDynamicApi } from "../../hooks/useApi";
import { API_ENDPOINTS } from "../../config/api";
import Toast from "../../utils/toast";
import { useTranslation } from "react-i18next";

const { Title, Text } = Typography;

const SubscriptionTransactionDetails = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);

  // API hooks
  const { execute: getTransaction, loading: transactionLoading } =
    useDynamicApi("GET");

  // Options from your model
  const PAYMENT_STATUS_OPTIONS = [
    {
      value: "SUCCESS",
      label: t("transactionManager.paymentStatusSuccess"),
      color: "green",
      icon: <CheckCircleOutlined />,
    },
    {
      value: "FAILED",
      label: t("transactionManager.paymentStatusFailed"),
      color: "red",
      icon: <CloseCircleOutlined />,
    },
    {
      value: "REFUNDED",
      label: t("transactionManager.paymentStatusRefunded"),
      color: "orange",
      icon: <UndoOutlined />,
    },
  ];

  const PAYMENT_METHOD_OPTIONS = [
    {
      value: "CARD",
      label: t("transactionManager.paymentMethodCard"),
      icon: <CreditCardOutlined />,
    },
    {
      value: "BANK",
      label: t("transactionManager.paymentMethodBank"),
      icon: <BankOutlined />,
    },
    {
      value: "PAYPAL",
      label: t("transactionManager.paymentMethodPaypal"),
      icon: <PayCircleOutlined />,
    },
    {
      value: "OTHER",
      label: t("transactionManager.paymentMethodOther"),
      icon: <FileTextOutlined />,
    },
  ];

  const TRANSACTION_TYPE_OPTIONS = [
    {
      value: "PURCHASE",
      label: t("transactionManager.transactionTypePurchase"),
      color: "blue",
    },
    {
      value: "RENEWAL",
      label: t("transactionManager.transactionTypeRenewal"),
      color: "green",
    },
    {
      value: "UPGRADE",
      label: t("transactionManager.transactionTypeUpgrade"),
      color: "purple",
    },
    {
      value: "REFUND",
      label: t("transactionManager.transactionTypeRefund"),
      color: "orange",
    },
  ];

  // Load transaction details
  const loadTransactionDetails = async () => {
    setLoading(true);
    try {
      const endpoint = `${API_ENDPOINTS.SUBSCRIPTION_TRANSACTIONS_UPDATE}/${id}`;
      const response = await getTransaction(endpoint);

      if (response?.status === true && response.data) {
        setTransaction(response.data);
      } else {
        Toast.error(t("transactionManager.failedFetchDetails"));
        navigate("/subscriptiontransaction");
      }
    } catch (error) {
      console.error("Error fetching transaction details:", error);
      Toast.error(error.message || t("transactionManager.failedFetchDetails"));
      navigate("/subscriptiontransaction");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      loadTransactionDetails();
    }
  }, [id]);

  // Format date
  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString(i18n.language === "nl" ? "nl-NL" : "en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount || isNaN(amount)) return "N/A";
    return new Intl.NumberFormat(i18n.language === "nl" ? "nl-NL" : "en-US", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Get payment status info
  const getPaymentStatusInfo = (status) => {
    const found = PAYMENT_STATUS_OPTIONS.find((opt) => opt.value === status);
    return (
      found || { label: status, color: "default", icon: <FileTextOutlined /> }
    );
  };

  // Get payment method info
  const getPaymentMethodInfo = (method) => {
    const found = PAYMENT_METHOD_OPTIONS.find((opt) => opt.value === method);
    return found || { label: method, icon: <CreditCardOutlined /> };
  };

  // Get transaction type info
  const getTransactionTypeInfo = (type) => {
    const found = TRANSACTION_TYPE_OPTIONS.find((opt) => opt.value === type);
    return found || { label: type, color: "default" };
  };

  if (loading || transactionLoading) return null;

  if (!transaction) {
    return (
      <Card>
        <div style={{ textAlign: "center", padding: "40px" }}>
          <Title level={3}>{t("transactionManager.noTransactionsFound")}</Title>
          <Button
            type="primary"
            onClick={() => navigate("/subscriptiontransaction")}
          >
            {t("common.previous")} {t("transactionManager.transactions")}
          </Button>
        </div>
      </Card>
    );
  }

  const paymentStatusInfo = getPaymentStatusInfo(transaction.payment_status);
  const paymentMethodInfo = getPaymentMethodInfo(transaction.payment_method);
  const transactionTypeInfo = getTransactionTypeInfo(
    transaction.transaction_type,
  );

  return (
    <div className="transaction-details-page">
      {/* Header with back button */}
      <div
        style={{
          marginBottom: "20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/subscriptiontransaction")}
          >
            {t("common.previous")} {t("transactionManager.transactions")}
          </Button>
          <Title level={4} style={{ margin: 0 }}>
            {t("transactionManager.transactionDetails")}
          </Title>
        </Space>
      </div>

      {/* Main Content */}
      <Row gutter={[24, 24]}>
        {/* Left Column - Transaction Summary */}
        <Col xs={24} lg={10}>
          <Card
            style={{
              borderRadius: "8px",
              boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
              height: "100%",
            }}
          >
            <div style={{ textAlign: "center", marginBottom: "24px" }}>
              <div
                style={{
                  width: "100px",
                  height: "100px",
                  borderRadius: "50%",
                  backgroundColor:
                    paymentStatusInfo.color === "green"
                      ? "#f6ffed"
                      : paymentStatusInfo.color === "red"
                        ? "#fff2f0"
                        : "#fff7e6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 16px",
                  border: `2px solid ${
                    paymentStatusInfo.color === "green"
                      ? "#52c41a"
                      : paymentStatusInfo.color === "red"
                        ? "#ff4d4f"
                        : "#faad14"
                  }`,
                }}
              >
                {paymentStatusInfo.icon}
              </div>
              <Title level={3} style={{ margin: "8px 0 4px" }}>
                {transaction.transaction_id}
              </Title>
              <Space wrap style={{ justifyContent: "center" }}>
                <Tag color={transactionTypeInfo.color}>
                  {transactionTypeInfo.label}
                </Tag>
                <Tag color={paymentStatusInfo.color}>
                  {paymentStatusInfo.label}
                </Tag>
              </Space>
            </div>

            <Divider style={{ margin: "16px 0" }} />

            {/* Amount Display */}
            <div style={{ textAlign: "center", marginBottom: "24px" }}>
              <Text type="secondary">{t("transactionManager.amountPaid")}</Text>
              <Title level={2} style={{ margin: "8px 0", color: "#1890ff" }}>
                {formatCurrency(transaction.amount_paid)}
              </Title>
            </div>

            <Divider style={{ margin: "16px 0" }} />

            {/* Quick Info */}
            <div style={{ marginBottom: "16px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  marginBottom: "12px",
                }}
              >
                <CalendarOutlined style={{ color: "#8c8c8c" }} />
                <div>
                  <Text
                    type="secondary"
                    style={{ display: "block", fontSize: "12px" }}
                  >
                    {t("transactionManager.transactionDate")}
                  </Text>
                  <Text strong>
                    {formatDateTime(transaction.transaction_date)}
                  </Text>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  marginBottom: "12px",
                }}
              >
                <UserOutlined style={{ color: "#8c8c8c" }} />
                <div>
                  <Text
                    type="secondary"
                    style={{ display: "block", fontSize: "12px" }}
                  >
                    {t("transactionManager.customerName")}
                  </Text>
                  <Text strong>{transaction.customer_name || "N/A"}</Text>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  marginBottom: "12px",
                }}
              >
                <MailOutlined style={{ color: "#8c8c8c" }} />
                <div>
                  <Text
                    type="secondary"
                    style={{ display: "block", fontSize: "12px" }}
                  >
                    {t("common.email")}
                  </Text>
                  <Text strong>{transaction.customer_email || "N/A"}</Text>
                </div>
              </div>

              {transaction.customer_phone && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    marginBottom: "12px",
                  }}
                >
                  <PhoneOutlined style={{ color: "#8c8c8c" }} />
                  <div>
                    <Text
                      type="secondary"
                      style={{ display: "block", fontSize: "12px" }}
                    >
                      {t("common.phone")}
                    </Text>
                    <Text strong>{transaction.customer_phone}</Text>
                  </div>
                </div>
              )}

              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <EnvironmentOutlined style={{ color: "#8c8c8c" }} />
                <div>
                  <Text
                    type="secondary"
                    style={{ display: "block", fontSize: "12px" }}
                  >
                    {t("transactionManager.environmentType")}
                  </Text>
                  <Tag
                    color={
                      transaction.environment_type === "INDIVIDUAL"
                        ? "blue"
                        : transaction.environment_type === "CLUB"
                          ? "green"
                          : "purple"
                    }
                  >
                    {transaction.environment_type === "INDIVIDUAL"
                      ? t("transactionManager.environmentIndividual")
                      : transaction.environment_type === "CLUB"
                        ? t("transactionManager.environmentClub")
                        : transaction.environment_type === "KNKV"
                          ? t("transactionManager.environmentKNKV")
                          : transaction.environment_type || "N/A"}
                  </Tag>
                </div>
              </div>
            </div>
          </Card>
        </Col>

        {/* Right Column - Detailed Information */}
        <Col xs={24} lg={14}>
          <Card
            style={{
              borderRadius: "8px",
              boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
            }}
          >
            <Descriptions bordered column={2}>
              <Descriptions.Item
                label={t("transactionManager.transactionId")}
                span={2}
              >
                <Space>
                  <IdcardOutlined />
                  <Text strong>{transaction.transaction_id}</Text>
                </Space>
              </Descriptions.Item>

              <Descriptions.Item
                label={t("transactionManager.transactionDate")}
                span={1}
              >
                <Space>
                  <CalendarOutlined />
                  {formatDateTime(transaction.transaction_date)}
                </Space>
              </Descriptions.Item>

              <Descriptions.Item
                label={t("transactionManager.transactionType")}
                span={1}
              >
                <Tag color={transactionTypeInfo.color}>
                  {transactionTypeInfo.label}
                </Tag>
              </Descriptions.Item>

              <Descriptions.Item
                label={t("transactionManager.customerName")}
                span={1}
              >
                <Space>
                  <UserOutlined />
                  {transaction.customer_name || "N/A"}
                </Space>
              </Descriptions.Item>

              <Descriptions.Item
                label={t("transactionManager.customerEmail")}
                span={1}
              >
                {transaction.customer_email ? (
                  <Space>
                    <MailOutlined />
                    <a href={`mailto:${transaction.customer_email}`}>
                      {transaction.customer_email}
                    </a>
                  </Space>
                ) : (
                  "N/A"
                )}
              </Descriptions.Item>

              <Descriptions.Item
                label={t("transactionManager.subscriptionPackage")}
                span={2}
              >
                <Space>
                  <FileTextOutlined />
                  {transaction.subscription_name || "N/A"}
                </Space>
              </Descriptions.Item>

              <Descriptions.Item
                label={t("transactionManager.amountPaid")}
                span={1}
              >
                <Space>
                  <DollarOutlined style={{ color: "#52c41a" }} />
                  <Text strong style={{ fontSize: "16px", color: "#52c41a" }}>
                    {formatCurrency(transaction.amount_paid)}
                  </Text>
                </Space>
              </Descriptions.Item>

              <Descriptions.Item
                label={t("transactionManager.paymentStatus")}
                span={1}
              >
                <Tag
                  color={paymentStatusInfo.color}
                  icon={paymentStatusInfo.icon}
                >
                  {paymentStatusInfo.label}
                </Tag>
              </Descriptions.Item>

              <Descriptions.Item
                label={t("transactionManager.paymentMethod")}
                span={1}
              >
                <Space>
                  {paymentMethodInfo.icon}
                  {paymentMethodInfo.label}
                </Space>
              </Descriptions.Item>

              <Descriptions.Item
                label={t("transactionManager.environmentType")}
                span={1}
              >
                <Tag
                  color={
                    transaction.environment_type === "INDIVIDUAL"
                      ? "blue"
                      : transaction.environment_type === "CLUB"
                        ? "green"
                        : "purple"
                  }
                >
                  {transaction.environment_type === "INDIVIDUAL"
                    ? t("transactionManager.environmentIndividual")
                    : transaction.environment_type === "CLUB"
                      ? t("transactionManager.environmentClub")
                      : transaction.environment_type === "KNKV"
                        ? t("transactionManager.environmentKNKV")
                        : transaction.environment_type || "N/A"}
                </Tag>
              </Descriptions.Item>

              <Descriptions.Item label={t("common.createdAt")} span={1}>
                <Space>
                  <ClockCircleOutlined />
                  {formatDateTime(transaction.created_at)}
                </Space>
              </Descriptions.Item>

              <Descriptions.Item label={t("common.lastUpdated")} span={1}>
                <Space>
                  <ClockCircleOutlined />
                  {formatDateTime(transaction.updated_at)}
                </Space>
              </Descriptions.Item>
            </Descriptions>

            {/* Additional Details Section */}
            {(transaction.payment_reference ||
              transaction.invoice_number ||
              transaction.notes) && (
              <>
                <Divider orientation="left" style={{ margin: "24px 0 16px" }}>
                  {t("transactionManager.additionalInformation")}
                </Divider>

                <Row gutter={[16, 16]}>
                  {transaction.payment_reference && (
                    <Col span={24}>
                      <Card
                        size="small"
                        type="inner"
                        title={t("transactionManager.paymentReference")}
                      >
                        <Text code>{transaction.payment_reference}</Text>
                      </Card>
                    </Col>
                  )}

                  {transaction.invoice_number && (
                    <Col span={12}>
                      <Card
                        size="small"
                        type="inner"
                        title={t("transactionManager.invoiceNumber")}
                      >
                        <Text strong>{transaction.invoice_number}</Text>
                      </Card>
                    </Col>
                  )}

                  {transaction.notes && (
                    <Col span={24}>
                      <Card
                        size="small"
                        type="inner"
                        title={t("transactionManager.notes")}
                      >
                        <Text>{transaction.notes}</Text>
                      </Card>
                    </Col>
                  )}
                </Row>
              </>
            )}

            {/* Timeline Section */}
            <Divider orientation="left" style={{ margin: "24px 0 16px" }}>
              {t("transactionManager.transactionTimeline")}
            </Divider>

            <Timeline mode="left" style={{ marginTop: "16px" }}>
              <Timeline.Item
                label={formatDateTime(transaction.created_at)}
                color="green"
                dot={<CheckCircleOutlined />}
              >
                <Text strong>{t("transactionManager.transactionCreated")}</Text>
                <br />
                <Text type="secondary">
                  {t("transactionManager.transactionInitiated")}
                </Text>
              </Timeline.Item>

              <Timeline.Item
                label={formatDateTime(transaction.transaction_date)}
                color={
                  transaction.payment_status === "SUCCESS"
                    ? "green"
                    : transaction.payment_status === "FAILED"
                      ? "red"
                      : "orange"
                }
                dot={paymentStatusInfo.icon}
              >
                <Text strong>
                  {t("transactionManager.payment")} {paymentStatusInfo.label}
                </Text>
                <br />
                <Text type="secondary">
                  {t("transactionManager.paymentWas")}{" "}
                  {paymentStatusInfo.label.toLowerCase()}{" "}
                  {t("transactionManager.onThisDate")}
                </Text>
              </Timeline.Item>

              <Timeline.Item
                label={formatDateTime(transaction.updated_at)}
                color="blue"
                dot={<ClockCircleOutlined />}
              >
                <Text strong>{t("transactionManager.lastUpdated")}</Text>
                <br />
                <Text type="secondary">
                  {t("transactionManager.transactionDetailsUpdated")}
                </Text>
              </Timeline.Item>
            </Timeline>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SubscriptionTransactionDetails;
